import { type JSX } from "react";

import { useDividerDrag } from "./hooks/useDividerDrag";

interface ResizableDividerProps {
  onDrag?: (clientX: number) => void;
  onDragStart?: () => void;
  onDragMove?: (clientX: number) => void;
  onDragEnd?: () => void;
}

/**
 * A draggable divider component for resizing panels.
 * Supports both mouse and touch interactions.
 */
export default function ResizableDivider_Component({
  onDrag,
  onDragStart,
  onDragMove,
  onDragEnd
}: ResizableDividerProps): JSX.Element {
  // onDrag is the original single-callback API, still used by callers that
  // don't distinguish start/move/end.
  const drag = useDividerDrag("x", { onDragStart, onDragMove: onDragMove ?? onDrag, onDragEnd });

  return (
    <div
      className="px-2 flex-shrink-0 cursor-col-resize flex items-center touch-none"
      onMouseDown={drag.onMouseDown}
      onTouchStart={drag.onTouchStart}
    >
      <div className="w-[3px] h-full bg-gray-300 hover:bg-blue-500 transition-colors duration-150 relative group">
        {/* Extended hover/touch target */}
        <div className="absolute inset-y-0 -left-2 -right-2 group-hover:bg-blue-500 group-hover:opacity-20" />
        {/* Pill-shaped drag handle — matches Figma "screen-resize" component */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[13px] h-[21px] bg-white border border-black/15 rounded-full flex items-center justify-center shadow-sm">
          {/* Grip icon — two vertical lines */}
          <svg width="5" height="9" viewBox="0 0 5 9" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-gray-400 group-hover:text-blue-500 transition-colors duration-150">
            <path d="M1 1v7M4 1v7" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
          </svg>
        </div>
      </div>
    </div>
  );
}
